import React, { FC, useEffect, useState } from "react";
import { tasksApis } from "../../../api/NewTasks";
import { useLocation } from "react-router";
import dayjs from "dayjs";
import {
  Empty,
  Flex,
  message,
  Spin,
  Card,
  Avatar,
  Tag,
  Typography,
  Row,
  Col,
  Tooltip,
  Progress,
} from "antd";
import {
  LoadingOutlined,
  CalendarOutlined,
  UserOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { projectsApis } from "../../../api/projects";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useWbsName } from "../shared/useWbsCodes";

const { Text } = Typography;

// Task Card Component
const TaskCard: React.FC<{
  task: any;
  isDragging?: boolean;
  groupBy: string;
}> = ({ task, isDragging, groupBy }) => {
  // The card subtitle is the activity name, which embeds a WBS code.
  const wbsName = useWbsName();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: task.id,
      disabled: groupBy !== "status", // Only enable dragging for status groupBy
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.95 : 1,
  };

  const getInitials = (name: string) => {
    if (!name) return "??";
    const words = name.split(" ");
    if (words.length > 1) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "magenta";
      case "high":
        return "red";
      case "medium":
        return "orange";
      case "low":
        return "green";
      default:
        return "default";
    }
  };

  // Check if task has pending approval for status change
  const hasPendingStatusApproval =
    groupBy === "status" &&
    task.approvalStatus === "Pending" &&
    task.updates &&
    "taskStatus" in task.updates;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Card
        size="small"
        style={{
          marginBottom: 8,
          cursor: groupBy === "status" ? "grab" : "default",
          boxShadow: isDragging ? "0 8px 24px rgba(37, 87, 214,.12)" : "0 1px 2px rgba(2,44,87,.05)",
          opacity: 1,
          border: isDragging ? "2px solid #2557d6" : undefined,
          background: "#fff",
        }}
        bodyStyle={{ padding: "12px" }}
      >
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text strong style={{ fontSize: "14px" }}>
            {task.name}
          </Text>
          {hasPendingStatusApproval && (
            <Tooltip
              title={`Status change to "${task.updates?.taskStatus}" pending approval`}
            >
              <ExclamationCircleFilled
                style={{
                  color: "#faad14",
                  // color: "#fa1414",
                  fontSize: "20px",
                  marginLeft: "8px",
                  fontWeight: "bold",
                }}
              />
            </Tooltip>
          )}
        </div>

        {task.description && (
          <div style={{ marginBottom: 8 }}>
            <Text
              type="secondary"
              style={{ fontSize: "12px" }}
              title={task.description}
            >
              {wbsName(task.description)}
            </Text>
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <Progress
            percent={task.taskCompletionPercentage ?? 0}
            size="small"
            strokeColor="#2557d6"
            trailColor="#eef1f6"
            showInfo={true}
            format={(percent) => `${percent}%`}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {task.priorityName && (
              <Tag color={getPriorityColor(task.priorityName)}>
                {task.priorityName}
              </Tag>
            )}

            <Tooltip
              title={dayjs(task.taskPlannedEndDate).format("DD-MMM-YYYY")}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "11px",
                  color: "#666",
                }}
              >
                <CalendarOutlined style={{ marginRight: "4px" }} />
                {dayjs(task.taskPlannedEndDate).format("DD-MMM")}
              </div>
            </Tooltip>
          </div>

          {task.taskOwnerName && (
            <Tooltip title={task.taskOwnerName}>
              <Avatar
                size={20}
                style={{ backgroundColor: "#2557d6", fontSize: "10px" }}
              >
                {getInitials(task.taskOwnerName)}
              </Avatar>
            </Tooltip>
          )}
        </div>
      </Card>
    </div>
  );
};

// Column Component
const KanbanColumn: React.FC<{
  column: any;
  tasks: any[];
  groupBy: string;
}> = ({ column, tasks, groupBy }) => {
  // In "taskList" mode the column heading is an activity name.
  const wbsName = useWbsName();
  const { isOver, setNodeRef } = useDroppable({
    id: column.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        backgroundColor: isOver ? "#e5ebfa" : "#ffffff",
        borderRadius: "8px",
        padding: "12px",
        height: "100%",
        width: "280px",
        minWidth: "280px",
        border: isOver ? "2px solid #2557d6" : "1px solid #dbdfea",
        transition: "all 0.2s ease",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 2px rgba(2,44,87,.05)",
      }}
    >
      <div
        style={{
          backgroundColor: "#eef1f6",
          color: "#022c57",
          textAlign: "center",
          padding: "8px",
          borderRadius: "6px",
          marginBottom: "12px",
          fontWeight: "bold",
          fontSize: "14px",
        }}
      >
        {groupBy === "taskList" ? wbsName(column.text) : column.text} (
        {tasks.length})
      </div>

      <SortableContext
        items={tasks.map((task) => task.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0, // Important: allows flex child to shrink below content size
          }}
        >
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} groupBy={groupBy} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
};

const KanbanView: FC<any> = ({ groupBy }) => {
  const [kanbanViewData, setKanbanViewData] = useState<any[]>([]);
  const [coloumnData, setColumnData] = useState<any[]>([]);
  const location = useLocation();
  const { id } = location.state || {};
  const [loading, setLoading] = useState(true);
  const [statusFilterData, setStatusFilterData] = useState<any[]>([]);
  const [isEmptyView, setIsEmptyView] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  // Drag and drop state
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1, // Start drag after moving 1 pixels
      },
    })
  );

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    fetchStatusAndThenData();
    return () => window.removeEventListener("resize", handleResize);
  }, [id, groupBy]);

  useEffect(() => {
    fetchStatusAndThenData();
  }, [id, groupBy]);

  const fetchStatusAndThenData = async () => {
    try {
      await fetchStatusFilterData(); // ensure statusFilterData is set
      await fetchKanbanViewData(); // now column logic will work
    } catch (err) {
      console.error("Error loading Kanban with status filters", err);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Optional: handle drag over events
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeTask = kanbanViewData.find((task) => task.id === active.id);
    if (!activeTask) {
      setActiveId(null);
      return;
    }

    // Get the target column ID - could be dropping on column or task
    let targetColumnId = over.id as string;

    // If dropping on a task, find which column that task belongs to
    const targetTask = kanbanViewData.find((task) => task.id === over.id);
    if (targetTask) {
      const columnField = groupBy === "taskList" ? "activity" : "status";
      targetColumnId = targetTask[columnField];
    }

    // Only handle status changes for groupBy status mode
    if (groupBy === "status") {
      const currentStatus = activeTask.status;
      if (currentStatus !== targetColumnId) {
        // Optimistic update - update UI immediately
        setKanbanViewData((prevData) =>
          prevData.map((task) =>
            task.id === active.id
              ? { ...task, status: targetColumnId }
              : task
          )
        );

        // Then sync with server
        handleTaskDrop(targetColumnId, activeTask.taskId, currentStatus);
      }
    }

    setActiveId(null);
  };

  const getTasksByColumn = (columnId: string) => {
    const columnField = groupBy === "taskList" ? "activity" : "status";
    return kanbanViewData.filter((task) => task[columnField] === columnId);
  };

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule(
        "TaskStatusChange"
      );
      const { data, status } = response;
      if (status) {
        setStatusFilterData(data);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchKanbanViewData = async () => {
    setLoading(true);
    setIsEmptyView(false);
    try {
      const apiResponse = await tasksApis.fetchKanbanView(id, {
        page: 1,
        pageSize: 100000,
        direction: "P",
        sortOrder: "ASC",
        pageDiff: 0,
        paginationId: null,
        projectIds: id,
      });

      const { data, status } = apiResponse;
      if (status) {
        const { list } = data;

        // Transform data to match expected format for custom Kanban
        const transformedData = list.map((task: any) => {
          return {
            ...task,
            id: task.taskId,
            name: task.taskName,
            status: task.derivedTaskStatus,
            description: task.activityName,
            activity: task.activityName,
            priorityName: task.priorityName,
            taskOwnerName: task.taskOwnerName,
            taskPlannedEndDate: task.taskPlannedEndDate,
            taskId: task.taskId,
            taskCompletionPercentage: task.taskCompletionPercentage,
          };
        });
        setKanbanViewData(transformedData);
        const groupField =
          groupBy === "taskList" ? "activityName" : "derivedTaskStatus";
        let columnList: string[] = [];
        console.log("statusFilterData", statusFilterData);
        if (groupField === "derivedTaskStatus") {
          columnList = statusFilterData;
        } else {
          columnList = Array.from(
            new Set(list.map((task: any) => task[groupField]).filter(Boolean))
          );
        }
        console.log("columnList", columnList);

        // Sort columns by predefined order if groupBy is status
        if (groupBy === "status") {
          const statusOrder = [
            "Open",
            "InProgress",
            "UnderQC",
            "Closed",
            "OnHold",
          ];
          columnList.sort((a, b) => {
            const indexA = statusOrder.indexOf(a);
            const indexB = statusOrder.indexOf(b);

            // If both are in the predefined order, sort by their position
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // If only A is in predefined order, it comes first
            if (indexA !== -1) return -1;
            // If only B is in predefined order, it comes first
            if (indexB !== -1) return 1;
            // If neither is in predefined order, maintain alphabetical order
            return a.localeCompare(b);
          });
        }

        const uniqueColumns = columnList.map((colName) => ({
          id: colName,
          text: colName,
        }));
        console.log("uniqueColumns", uniqueColumns);
        setColumnData(uniqueColumns);

        const isEmpty =
          transformedData.length === 0 ||
          uniqueColumns.length === 0 ||
          (groupBy !== "taskList" &&
            transformedData.every(
              (task: any) => !columnList.includes(task.status)
            ));

        setIsEmptyView(isEmpty);
      }
    } catch (error) {
      console.error("Error fetching kanban view data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskDrop = async (
    targetColumn: any,
    taskId: number,
    previousStatus?: string
  ) => {
    try {
      const response = await tasksApis.statusChangeTask(taskId, targetColumn);
      const { status } = response;
      if (status) {
        message.success(response?.message);
        // No need to refetch - optimistic update already applied
      } else {
        message.error(response?.message);
        // Revert the optimistic update on failure
        if (previousStatus) {
          setKanbanViewData((prevData) =>
            prevData.map((task) =>
              task.taskId === taskId
                ? { ...task, status: previousStatus }
                : task
            )
          );
        }
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      message.error("Failed to update task status");
      // Revert the optimistic update on error
      if (previousStatus) {
        setKanbanViewData((prevData) =>
          prevData.map((task) =>
            task.taskId === taskId
              ? { ...task, status: previousStatus }
              : task
          )
        );
      }
    }
  };

  const activeTask = activeId
    ? kanbanViewData.find((task) => task.id === activeId)
    : null;

  console.log("isEmptyView", coloumnData, kanbanViewData, isEmptyView);

  return (
    <>
      {loading ? (
        <Flex align="center" gap="middle" className="pageLoader">
          <Spin
            indicator={<LoadingOutlined spin />}
            className="m-auto"
            size="large"
          />
        </Flex>
      ) : isEmptyView ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            backgroundColor: "white",
          }}
        >
          <Empty description="No tasks found or no matching status for the tasks" />
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            overflow: "hidden",
            border: "1px solid #e4e9f0",
            borderRadius: "8px",
            backgroundColor: "#fafafa",
          }}
        >
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div
              style={{
                height: "100%",
                width: "100%",
                overflowX: "auto",
                overflowY: "hidden",
                padding: "16px",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  display: "flex",
                  height: "calc(100% - 16px)",
                  gap: "16px",
                  width: "fit-content",
                  minWidth: "100%",
                }}
              >
                {coloumnData.map((column) => {
                  const columnTasks = getTasksByColumn(column.id);
                  return (
                    <KanbanColumn
                      key={column.id}
                      column={column}
                      tasks={columnTasks}
                      groupBy={groupBy}
                    />
                  );
                })}
              </div>
            </div>

            <DragOverlay>
              {activeTask ? (
                <TaskCard task={activeTask} isDragging groupBy={groupBy} />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}
    </>
  );
};

export default KanbanView;
